import type { TableFormColumn } from '@common/types/templateFormFields';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import { errorSnackbar } from '@/utils/snackbar';
import QuestionSelectComponent from './QuestionSelectComponent';
import TableColumnField from './TableColumnField';

interface ColumnError {
    message: string;
    state: boolean;
    index: number;
}

interface TableColumnsComponentProps {
    disabled?: boolean;
    columns: TableFormColumn[];
    isSuperAdmin?: boolean;
    onChange: (columns: TableFormColumn[]) => void;
}

/** Table column options are stored as strings; the dropdown editor already emits strings here. */
function toText(value: string | number | undefined): string {
    return value === undefined ? '' : String(value);
}

export default function TableColumnsComponent({
    disabled = false,
    columns,
    isSuperAdmin,
    onChange,
}: TableColumnsComponentProps) {
    const dragIndex = useRef<number | undefined>(undefined);
    const theme = useTheme();
    const [errors, setErrors] = useState<ColumnError[]>([]);
    const [editingSelectColumnIndex, setEditingSelectColumnIndex] = useState<number | null>(null);

    const handleColumnChange = (index: number, updatedColumn: TableFormColumn) => {
        const sameLabel = columns.find(
            (col, i) => i !== index && col.label === updatedColumn.label && updatedColumn.label,
        );

        if (sameLabel) {
            setErrors((prev) => [
                ...prev.filter((e) => e.index !== index),
                { message: 'Column name already exists', state: true, index },
            ]);
        } else {
            setErrors((prev) => prev.filter((e) => e.index !== index));
        }

        const updatedColumns = columns.map((col, i) => (i === index ? updatedColumn : col));
        onChange(updatedColumns);
    };

    const handleRemoveColumn = (index: number) => {
        if (columns.length <= 1) {
            errorSnackbar('A table must have at least one column.');
            return;
        }
        const updatedColumns = columns.filter((_, i) => i !== index);
        setErrors((prev) => prev.filter((e) => e.index !== index));
        if (editingSelectColumnIndex === index) {
            setEditingSelectColumnIndex(null);
        }
        onChange(updatedColumns);
    };

    const handleDrop = (dropIndex: number) => {
        const updatedColumns = [...columns];
        if (dragIndex.current !== undefined) {
            const draggedItem = updatedColumns.splice(dragIndex.current, 1)[0];
            updatedColumns.splice(dropIndex, 0, draggedItem);
            dragIndex.current = undefined;
        }
        onChange(updatedColumns);
    };

    const handleAddColumn = () => {
        onChange([
            ...columns,
            {
                key: '',
                label: '',
                type: 'text',
            },
        ]);
    };

    const isAddDisabled =
        disabled ||
        columns.some((col) => !col.label) ||
        columns.some((col, index) =>
            columns.slice(0, index).some((prevCol) => prevCol.label === col.label),
        );

    const selectColumn =
        editingSelectColumnIndex !== null ? columns[editingSelectColumnIndex] : null;

    return (
        <Box
            component="div"
            onDragOver={(e) => e.preventDefault()}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '16px',
            }}
        >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Define the columns for your table. Each row in the table will have these fields.
            </Typography>

            {columns.map((column, index) => (
                <Box key={index} sx={{ width: '100%' }}>
                    <TableColumnField
                        index={index + 1}
                        column={column}
                        error={errors.find((e) => e.index === index)}
                        disabled={disabled}
                        onChange={(col) => handleColumnChange(index, col)}
                        onRemove={() => handleRemoveColumn(index)}
                        onDragStart={(i) => (dragIndex.current = i)}
                        onDrop={handleDrop}
                    />
                    {column.type === 'select' && (
                        <Box sx={{ marginLeft: '44px', marginTop: '8px' }}>
                            <Button
                                variant="text"
                                size="small"
                                sx={{ textTransform: 'none' }}
                                onClick={() =>
                                    setEditingSelectColumnIndex(
                                        editingSelectColumnIndex === index ? null : index,
                                    )
                                }
                            >
                                {editingSelectColumnIndex === index
                                    ? 'Hide dropdown options'
                                    : `Edit dropdown options (${column.selectOptions?.length || 0})`}
                            </Button>
                        </Box>
                    )}
                </Box>
            ))}

            <Button
                disabled={isAddDisabled}
                sx={{ textTransform: 'initial' }}
                variant="text"
                onClick={handleAddColumn}
            >
                <AddIcon /> Add column
            </Button>

            {selectColumn && editingSelectColumnIndex !== null && (
                <>
                    <Divider sx={{ width: '100%', marginY: 2 }} />
                    <Box sx={{ width: '100%' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2 }}>
                            Dropdown options for "
                            {selectColumn.label || `Column ${editingSelectColumnIndex + 1}`}"
                        </Typography>
                        <QuestionSelectComponent
                            fieldData={{}}
                            isSuperAdmin={isSuperAdmin}
                            error={[]}
                            onError={() => {}}
                            disabled={disabled}
                            selectOptions={
                                selectColumn.selectOptions && selectColumn.selectOptions.length > 0
                                    ? selectColumn.selectOptions.map((opt) => ({
                                          selectItemKey: opt.selectItemKey || opt.selectItemValue,
                                          selectItemLabel: opt.selectItemLabel,
                                          selectItemValue: opt.selectItemValue,
                                          isNewAdded: false,
                                      }))
                                    : [
                                          {
                                              selectItemKey: '',
                                              selectItemLabel: '',
                                              selectItemValue: '',
                                              isNewAdded: true,
                                          },
                                          {
                                              selectItemKey: '',
                                              selectItemLabel: '',
                                              selectItemValue: '',
                                              isNewAdded: true,
                                          },
                                      ]
                            }
                            onChange={(options) => {
                                const updatedColumn: TableFormColumn = {
                                    ...selectColumn,
                                    selectOptions: options.map((opt) => ({
                                        selectItemKey: toText(
                                            opt.selectItemKey || opt.selectItemValue,
                                        ),
                                        selectItemLabel: opt.selectItemLabel ?? '',
                                        selectItemValue: toText(opt.selectItemValue),
                                    })),
                                };
                                handleColumnChange(editingSelectColumnIndex, updatedColumn);
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
}
